import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell } from 'recharts';
import { CheckCircle, Circle } from 'lucide-react';
import api from '../services/api';
import { ActionItem, MoodEntry } from '../types';

interface HistoryDashboardProps {
    meetingId: number; // Meeting Series ID
    currentUser: { id: number };
}

interface RatingsDataPoint {
    date: string;
    team: Record<string, number>;
    user?: Record<string, number>;
}

export const HistoryDashboard: React.FC<HistoryDashboardProps> = ({ meetingId, currentUser }) => {
    const [activeTab, setActiveTab] = useState<'mood' | 'ratings' | 'actions'>('mood');
    const [actionItems, setActionItems] = useState<ActionItem[]>([]);
    const [moodHistory, setMoodHistory] = useState<MoodEntry[]>([]);
    const [ratingsHistory, setRatingsHistory] = useState<RatingsDataPoint[]>([]);
    const [showOnlyMine, setShowOnlyMine] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (activeTab === 'mood') {
                    const res = await api.get(`/meetings/${meetingId}/mood-history`);
                    setMoodHistory(res.data);
                } else if (activeTab === 'ratings') {
                    const res = await api.get(`/meetings/${meetingId}/ratings`);
                    setRatingsHistory(res.data);
                } else if (activeTab === 'actions') {
                    const res = await api.get(`/meetings/${meetingId}/action-items`);
                    setActionItems(res.data);
                }
            } catch (err) {
                console.error("Failed to fetch history", err);
            }
        };
        fetchData();
    }, [meetingId, activeTab]);

    const toggleActionStatus = async (id: number) => {
        try {
            const item = actionItems.find(i => i.id === id);
            if (!item) return;

            const newStatus = item.status === 'open' ? 'completed' : 'open';

            // Optimistic update
            setActionItems(items => items.map(i =>
                i.id === id ? { ...i, status: newStatus } : i
            ));

            await api.patch(`/meetings/${meetingId}/action-items/${id}`, { status: newStatus });

        } catch (err) {
            console.error("Failed to update status", err);
            // Revert on error
            // (In a real app, refetch or undo optimistic update)
        }
    };

    // Prepare Mood Data
    const formattedMoodData = useMemo(() => {
        return moodHistory
            .filter(entry => !showOnlyMine || entry.user_id === currentUser.id)
            .map(entry => ({
                ...entry,
                x: entry.x_coord,
                y: entry.y_coord,
                userName: entry.user?.full_name || 'Unknown',
                userColor: entry.user?.color_preference || '#8884d8',
                // @ts-ignore - session is populated by backend
                date: entry.session ? new Date(entry.session.date).toLocaleDateString() : 'Unknown'
            }));
    }, [moodHistory, showOnlyMine]);

    // Extract unique users for Legend
    const uniqueUsers = useMemo(() => {
        const users = new Map();
        formattedMoodData.forEach(d => {
            if (d.userName && !users.has(d.userName)) {
                users.set(d.userName, d.userColor);
            }
        });
        return Array.from(users.entries()).map(([name, color]) => ({ name, color }));
    }, [formattedMoodData]);

    // Prepare Ratings Data for Chart
    const formattedRatingsData = useMemo(() => {
        return ratingsHistory.map(point => ({
            date: point.date,
            teamAvg: (point.team.together + point.team.reflections + point.team.topic + point.team.value) / 4,
            userAvg: point.user ? (point.user.together + point.user.reflections + point.user.topic + point.user.value) / 4 : null,
            // Detailed breakdown (optional, keeping averages for clarity per request)
        }));
    }, [ratingsHistory]);

    // Custom Tooltip for Mood
    const CustomMoodTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white p-2 border border-gray-200 shadow-lg rounded text-sm">
                    <p className="font-bold">{data.userName}</p>
                    <p className="text-gray-500">{data.date}</p>
                    <p>Energy: {data.y}</p>
                    <p>Pleasantness: {data.x}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="w-full max-w-6xl mx-auto p-6">
            <div className="flex gap-8 border-b border-gray-200 mb-8">
                {['mood', 'ratings', 'actions'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab as any)}
                        className={`pb-4 text-lg font-medium capitalize transition-colors ${activeTab === tab
                            ? 'border-b-2 border-black text-black'
                            : 'text-gray-500 hover:text-gray-800'
                            }`}
                    >
                        {tab === 'actions' ? 'Action Board' : `${tab} History`}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 min-h-[400px]">
                {activeTab === 'mood' && (
                    <div className="h-[500px] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">Mood History</h3>
                            <label className="flex items-center gap-2 cursor-pointer bg-gray-50 px-3 py-1.5 rounded-lg border hover:border-gray-300 transition-colors">
                                <input
                                    type="checkbox"
                                    checked={showOnlyMine}
                                    onChange={(e) => setShowOnlyMine(e.target.checked)}
                                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Show only my history</span>
                            </label>
                        </div>

                        {/* Legend */}
                        <div className="flex flex-wrap gap-4 mb-4 justify-center">
                            {uniqueUsers.map(u => (
                                <div key={u.name} className="flex items-center gap-2 text-sm">
                                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: u.color }} />
                                    <span>{u.name}</span>
                                </div>
                            ))}
                        </div>

                        <div className="flex-1 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                    <CartesianGrid />
                                    <XAxis type="number" dataKey="x" name="Pleasantness" domain={[0, 7]} />
                                    <YAxis type="number" dataKey="y" name="Energy" domain={[0, 7]} />
                                    <Tooltip content={<CustomMoodTooltip />} />
                                    <Scatter name="Mood" data={formattedMoodData} fill="#8884d8">
                                        {formattedMoodData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.userColor} />
                                        ))}
                                    </Scatter>
                                </ScatterChart>
                            </ResponsiveContainer>
                        </div>
                        <p className="text-center text-gray-500 mt-2 text-sm">
                            Mood entries across all sessions. Hover for details.
                        </p>
                    </div>
                )}

                {activeTab === 'ratings' && (
                    <div className="h-[400px]">
                        <h3 className="text-xl font-bold mb-4">Overall Ratings Performance</h3>
                        <div style={{ width: '100%', height: 350 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={formattedRatingsData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis domain={[0, 10]} />
                                    <Tooltip />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="teamAvg"
                                        name="Team Average"
                                        stroke="#8884d8"
                                        strokeWidth={3}
                                        activeDot={{ r: 8 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="userAvg"
                                        name="My Average"
                                        stroke="#82ca9d"
                                        strokeWidth={3}
                                        strokeDasharray="5 5"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <p className="text-center text-gray-500 mt-2">
                            Comparing your average ratings vs. team average over time.
                        </p>
                    </div>
                )}

                {activeTab === 'actions' && (
                    <div>
                        <h3 className="text-xl font-bold mb-6">Start / Stop / Continue</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {['start', 'stop', 'continue'].map((type) => (
                                <div key={type} className="bg-gray-50 rounded-xl p-4">
                                    <h4 className="font-bold uppercase text-sm text-gray-500 mb-4 flex items-center gap-2">
                                        {type === 'start' && <span className="w-2 h-2 rounded-full bg-green-500" />}
                                        {type === 'stop' && <span className="w-2 h-2 rounded-full bg-red-500" />}
                                        {type === 'continue' && <span className="w-2 h-2 rounded-full bg-blue-500" />}
                                        {type}
                                    </h4>
                                    <div className="space-y-3">
                                        {actionItems.filter(i => i.type === type).map(item => (
                                            <div
                                                key={item.id}
                                                className={`
                          p-3 bg-white rounded-lg border shadow-sm flex items-start gap-3 transition-all
                          ${item.status === 'completed' ? 'opacity-50' : 'hover:shadow-md'}
                        `}
                                            >
                                                <button
                                                    onClick={() => toggleActionStatus(item.id)}
                                                    className="mt-0.5 text-gray-400 hover:text-green-600 transition-colors"
                                                >
                                                    {item.status === 'completed' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5" />}
                                                </button>
                                                <span className={`text-sm ${item.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                                                    {item.content}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
